import { authenticatedInstance } from '../index/index.service';
import type { AreaWithTablesView, AreaTableShape, FloorDecorElement, IdResponse } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type AreaView = AreaWithTablesView;
export type { AreaTableShape, AreaTableView, FloorDecorElement, FloorDecorKind } from 'shared-models/browser';

export type CreateAreaRequest = {
  name: string;
};

export type UpdateAreaRequest = {
  name?: string;
  floorDecor?: FloorDecorElement[];
  requiresFireApproval?: boolean;
};

export type CreateAreaTableRequest = {
  number: string;
  shape: AreaTableShape;
};

export type UpdateAreaTableRequest = {
  isFree?: boolean;
  number?: string;
  shape?: AreaTableShape;
  positionX?: number;
  positionY?: number;
  width?: number;
  height?: number;
  minimumOrder?: number;
  minimumOrderPerDiner?: number;
};

export type AreaExportPayload = {
  version: 1;
  name: string;
  requiresFireApproval: boolean;
  floorDecor: FloorDecorElement[];
  tables: {
    number: string;
    shape: AreaTableShape;
    positionX: number;
    positionY: number;
    width: number;
    height: number;
    minimumOrder: number;
    minimumOrderPerDiner: number;
  }[];
};

export type AreaImportResult = {
  id: number;
  name: string;
  areaRenamed: boolean;
  tablesCreated: number;
  renamedTables: { from: string; to: string }[];
};

class AreasService {
  // Summary: Loads active tenant areas for POS floor browsing mode without manager edit controls.
  // Details: docs/function-library/web/services.md#areas-service
  async listActive(tenantId: string): Promise<AreaView[]> {
    try {
      const response = await authenticatedInstance.get<AreaView[]>(`/areas/tenant/${tenantId}/active`);
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Loads manager settings area list (including inactive rows) using employee session authorization for area edit mode.
  // Details: docs/function-library/web/services.md#areas-service
  async listForSettings(tenantId: string, employeeSession: string): Promise<AreaView[]> {
    try {
      const response = await authenticatedInstance.get<AreaView[]>(`/areas/tenant/${tenantId}`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Creates area canvas rows from manager edit mode while forwarding employee session header required by backend guard.
  // Details: docs/function-library/web/services.md#areas-service
  async create(tenantId: string, payload: CreateAreaRequest, employeeSession: string): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>(`/areas/tenant/${tenantId}`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Updates manager-selected area metadata in area edit mode while preserving manager authorization context.
  // Details: docs/function-library/web/services.md#areas-service
  async update(areaId: number, payload: UpdateAreaRequest, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/areas/${areaId}`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Swaps two areas' display order (area-bar move-left/move-right).
  // Details: docs/function-library/web/services.md#areas-service
  async reorder(areaId: number, otherAreaId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/areas/${areaId}/reorder-with/${otherAreaId}`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Deactivates area rows from manager edit mode while forwarding employee session authorization.
  // Details: docs/function-library/web/services.md#areas-service
  async deactivate(areaId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/areas/${areaId}/deactivate`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Reactivates area rows from manager edit mode while forwarding employee session authorization.
  // Details: docs/function-library/web/services.md#areas-service
  async activate(areaId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/areas/${areaId}/activate`,
        {},
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Creates a new table layout entry under a selected area canvas in manager edit mode.
  // Details: docs/function-library/web/services.md#areas-service
  async createTable(
    areaId: number,
    payload: CreateAreaTableRequest,
    employeeSession: string,
  ): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>(`/areas/${areaId}/tables`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Saves manager edits for an existing table layout entry (number, shape, size, and location).
  // Details: docs/function-library/web/services.md#areas-service
  async updateTable(
    areaId: number,
    tableId: number,
    payload: UpdateAreaTableRequest,
    employeeSession: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(
        `/areas/${areaId}/tables/${tableId}`,
        payload,
        {
          headers: {
            'x-employee-session': employeeSession,
          },
        },
      );
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Permanently removes a table layout entry from the canvas in manager edit mode.
  // Details: docs/function-library/web/services.md#areas-service
  async deleteTable(areaId: number, tableId: number, employeeSession: string): Promise<void> {
    try {
      await authenticatedInstance.delete<void>(`/areas/${areaId}/tables/${tableId}`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Downloads one area as a portable JSON payload so a manager can rebuild it elsewhere.
  // Details: docs/function-library/web/services.md#areas-service
  async exportArea(areaId: number, employeeSession: string): Promise<AreaExportPayload> {
    try {
      const response = await authenticatedInstance.get<AreaExportPayload>(`/areas/${areaId}/export`, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Rebuilds an exported area under the current tenant; backend remaps taken names/numbers.
  // Details: docs/function-library/web/services.md#areas-service
  async importArea(tenantId: string, payload: AreaExportPayload, employeeSession: string): Promise<AreaImportResult> {
    try {
      const response = await authenticatedInstance.post<AreaImportResult>(`/areas/tenant/${tenantId}/import`, payload, {
        headers: {
          'x-employee-session': employeeSession,
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const areasService = new AreasService();





